import { createContext, useContext } from "react";

/**
 * Identity of the record whose value a {@link SchemaForm} is currently
 * rendering.
 *
 * Most schema-form elements render purely from the element schema + the form
 * data and need no knowledge of the surrounding record. The
 * `form-incoming-reference` element is the exception: it shows the records that
 * reference the CURRENT record (its inbound / reverse references), so it must
 * know which record is being viewed. That identity is not present in the
 * element schema or the form data — it lives on the record screen. Rather than
 * thread `record_type`/`id` through the recursive SchemaFormFragment tree, the
 * SchemaForm root publishes it here and the consuming element reads it via
 * {@link useSchemaFormRecord} (precedent: SchemaFormArrayItemIdProvider).
 *
 * The value is OPTIONAL: SchemaForm is also used in contexts with no backing
 * record (flow input_schema builders, the test-form preview, message forms),
 * where it is simply absent. A `form-incoming-reference` element only appears in
 * a custom object's value schema, so in practice the identity is present
 * wherever the element actually renders; elements that find it absent degrade
 * gracefully instead of crashing.
 */
export interface SchemaFormRecordIdentity {
    /** Backend record type of the current record, e.g. "CUSTOM_OBJECT". */
    recordType?: string;
    /** Id of the current record. */
    recordId?: string;
}

const SchemaFormRecordContext = createContext<SchemaFormRecordIdentity>({});

export const SchemaFormRecordProvider = ({
    value,
    children,
}: {
    value: SchemaFormRecordIdentity;
    children: React.ReactNode;
}) => (
    <SchemaFormRecordContext.Provider value={value}>{children}</SchemaFormRecordContext.Provider>
);

/** Identity of the record the surrounding SchemaForm is rendering ({} if none). */
export function useSchemaFormRecord(): SchemaFormRecordIdentity {
    return useContext(SchemaFormRecordContext);
}
